import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { 
  Shield, Users, Gift, Clock, AlertTriangle, 
  Check, X, Search, RefreshCw, Crown, Zap,
  TrendingUp, Calendar, Package, ArrowLeft, User
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import UserProfileModal from "../components/admin/UserProfileModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BASE_URL } from "../lib/api";

const API_URL = BASE_URL;

export default function AdminTrialManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [trials, setTrials] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  
  // Grant trial form state
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [grantType, setGrantType] = useState("extended"); // extended or package
  const [grantForm, setGrantForm] = useState({
    user_email: "",
    video_limit: 10,
    max_duration: 30,
    duration_days: 14,
    ugc_access: false,
    plan: "premium",
    reason: "",
  });
  const [grantLoading, setGrantLoading] = useState(false);

  // User lookup state - initialize from URL params if present
  const [searchQuery, setSearchQuery] = useState(searchParams.get("email") || searchParams.get("search") || "");
  const [userDetails, setUserDetails] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // User profile modal state
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  
  const handleViewUserProfile = (userId) => {
    setSelectedUserId(userId);
    setUserProfileOpen(true);
  };
  
  const handleViewUserProfileByEmail = async (email) => {
    if (!email) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users?search=${encodeURIComponent(email)}&limit=1`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setSelectedUserId(data[0].id || data[0]._id);
          setUserProfileOpen(true);
        } else {
          toast.error(`User not found: ${email}`);
        }
      }
    } catch (error) {
      console.error("Error looking up user:", error);
      toast.error("Failed to load user profile");
    }
  };

  // Global trial settings state
  const [globalSettings, setGlobalSettings] = useState({
    enabled: true,
    trial_days: 1,
    plan: "standard",
    video_limit: 3,
    max_duration: 10,
    ugc_access: false,
    caption_access: true,
    ghost_mode_access: false,
    stock_footage_access: true,
    welcome_message: "Welcome! Enjoy your free trial.",
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const token = localStorage.getItem("auth_token");

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  // Auto-search if email passed in URL params
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam && isAdmin && !userDetails) {
      setSearchQuery(emailParam);
      handleSearchUser(emailParam);
    }
  }, [isAdmin, searchParams]);

  const checkAdminAndLoad = async () => {
    try {
      // Check if user is admin
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = await response.json();
      
      if (!userData.is_admin) {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }
      
      setIsAdmin(true);
      await Promise.all([loadStats(), loadTrials(), loadGlobalSettings()]);
    } catch (error) {
      console.error("Auth error:", error);
      toast.error("Authentication failed");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const loadGlobalSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/trial/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setGlobalSettings(data.settings);
        }
      }
    } catch (error) {
      console.error("Failed to load global settings:", error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveGlobalSettings = async () => {
    setSettingsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/trial/settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(globalSettings),
      });
      
      if (response.ok) {
        const data = await response.json();
        setGlobalSettings(data.settings);
        toast.success("Global trial settings saved!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/trial/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const loadTrials = async () => {
    try {
      // Don't pass status param if "all" is selected
      const url = statusFilter && statusFilter !== "all"
        ? `${API_URL}/api/admin/trial/list?status=${statusFilter}`
        : `${API_URL}/api/admin/trial/list`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTrials(data.grants || []);
      }
    } catch (error) {
      console.error("Failed to load trials:", error);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadTrials();
    }
  }, [statusFilter, isAdmin]);

  const handleGrantTrial = async () => {
    if (!grantForm.user_email) {
      toast.error("Please enter user email");
      return;
    }

    setGrantLoading(true);
    try {
      const endpoint = grantType === "extended" 
        ? `${API_URL}/api/admin/trial/grant-extended`
        : `${API_URL}/api/admin/trial/grant-package`;
      
      const body = grantType === "extended" 
        ? {
            user_email: grantForm.user_email,
            video_limit: grantForm.video_limit,
            max_duration: grantForm.max_duration,
            duration_days: grantForm.duration_days,
            ugc_access: grantForm.ugc_access,
            reason: grantForm.reason || null,
          }
        : {
            user_email: grantForm.user_email,
            plan: grantForm.plan,
            duration_days: grantForm.duration_days,
            reason: grantForm.reason || null,
          };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || "Trial granted successfully!");
        setGrantModalOpen(false);
        setGrantForm({
          user_email: "",
          video_limit: 10,
          max_duration: 30,
          duration_days: 14,
          ugc_access: false,
          plan: "premium",
          reason: "",
        });
        await Promise.all([loadStats(), loadTrials()]);
      } else {
        toast.error(data.detail || "Failed to grant trial");
      }
    } catch (error) {
      console.error("Grant error:", error);
      toast.error("Failed to grant trial");
    } finally {
      setGrantLoading(false);
    }
  };

  const handleRevokeTrial = async (userEmail) => {
    if (!confirm(`Revoke trial for ${userEmail}?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/trial/revoke/${encodeURIComponent(userEmail)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Trial revoked");
        await Promise.all([loadStats(), loadTrials()]);
        if (userDetails?.user_email === userEmail) {
          handleSearchUser(userEmail);
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to revoke trial");
      }
    } catch (error) {
      toast.error("Failed to revoke trial");
    }
  };

  const handleResetTrial = async (userEmail, extendDays = 0) => {
    const message = extendDays > 0 
      ? `Reset trial for ${userEmail} and extend by ${extendDays} days?`
      : `Reset video count for ${userEmail}? This will allow them to use their remaining trial.`;
    
    if (!confirm(message)) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/trial/reset`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_email: userEmail,
          reset_video_count: true,
          extend_days: extendDays
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Trial reset successfully");
        await Promise.all([loadStats(), loadTrials()]);
        if (userDetails?.user_email === userEmail) {
          handleSearchUser(userEmail);
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to reset trial");
      }
    } catch (error) {
      toast.error("Failed to reset trial");
    }
  };

  const handleRenewTrial = async (userEmail, settings = {}) => {
    const defaultSettings = {
      duration_days: 14,
      video_limit: 10,
      max_duration: 30,
      plan: "premium",
      ugc_access: false,
      reason: "Trial renewed by admin"
    };
    const finalSettings = { ...defaultSettings, ...settings, user_email: userEmail };

    if (!confirm(`Renew trial for ${userEmail}?\n\nNew trial: ${finalSettings.plan} plan, ${finalSettings.duration_days} days, ${finalSettings.video_limit} videos`)) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/trial/renew`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(finalSettings)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Trial renewed successfully");
        await Promise.all([loadStats(), loadTrials()]);
        // Always refresh user details after renewal to show updated trial status
        if (userDetails?.user_email === userEmail) {
          await handleSearchUser(userEmail);
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to renew trial");
      }
    } catch (error) {
      console.error("Renew trial error:", error);
      toast.error("Failed to renew trial");
    }
  };

  const handleSearchUser = async (queryOverride) => {
    const searchTerm = (queryOverride || searchQuery || "").trim();
    if (!searchTerm) {
      toast.error("Please enter an email or username");
      return;
    }

    setSearchLoading(true);
    setUserDetails(null); // Clear previous results
    
    try {
      const response = await fetch(`${API_URL}/api/admin/trial/user/${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setUserDetails(data);
        if (!data.trial_history?.length) {
          toast.info("User found but has no trial history");
        }
      } else {
        let errorMsg = "User not found";
        try {
          const data = await response.json();
          errorMsg = data.detail || data.message || errorMsg;
        } catch (e) {
          // Response wasn't JSON
          if (response.status === 404) {
            errorMsg = `User "${searchTerm}" not found by email or name`;
          } else if (response.status === 401) {
            errorMsg = "Session expired. Please login again.";
          } else {
            errorMsg = `Error: ${response.status} ${response.statusText}`;
          }
        }
        toast.error(errorMsg);
        setUserDetails(null);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed. Please check your connection.");
      setUserDetails(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status, expiresAt) => {
    const isExpired = expiresAt && new Date(expiresAt) < new Date();
    
    if (status === "active" && !isExpired) {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Active</Badge>;
    } else if (status === "active" && isExpired) {
      return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Expired</Badge>;
    } else if (status === "revoked") {
      return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Revoked</Badge>;
    } else {
      return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin")}
              className="text-muted-foreground hover:text-white"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Shield className="w-6 h-6 text-cyan-400" />
                Admin Trial Management
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                Manage user trials for marketing and promotions
              </p>
            </div>
          </div>
          <Button
            onClick={() => setGrantModalOpen(true)}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
            data-testid="grant-trial-btn"
          >
            <Gift className="w-4 h-4 mr-2" />
            Grant Trial
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Active Trials</p>
                    <p className="text-2xl font-bold text-green-400">{stats.active_trials}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Expired</p>
                    <p className="text-2xl font-bold text-amber-400">{stats.expired_trials}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Last 7 Days</p>
                    <p className="text-2xl font-bold text-cyan-400">{stats.grants_last_7_days}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-cyan-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-sm">Total Grants</p>
                    <p className="text-2xl font-bold text-white">{stats.total_grants}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-slate-600/50 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="settings" className="space-y-4">
          <TabsList className="bg-slate-800/50 border border-slate-700">
            <TabsTrigger value="settings" className="data-[state=active]:bg-cyan-500/20">
              <Zap className="w-4 h-4 mr-2" />
              Default Settings
            </TabsTrigger>
            <TabsTrigger value="trials" className="data-[state=active]:bg-cyan-500/20">
              <Gift className="w-4 h-4 mr-2" />
              Trial Grants
            </TabsTrigger>
            <TabsTrigger value="lookup" className="data-[state=active]:bg-cyan-500/20">
              <Search className="w-4 h-4 mr-2" />
              User Lookup
            </TabsTrigger>
          </TabsList>

          {/* Global Trial Settings Tab */}
          <TabsContent value="settings">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  Default Trial Settings for New Users
                </CardTitle>
                <CardDescription>
                  Configure the automatic trial that every new user receives upon signup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {settingsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-cyan-400" />
                  </div>
                ) : (
                  <>
                    {/* Enable/Disable Auto Trial */}
                    <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div>
                        <Label className="text-white font-medium">Auto-Trial for New Users</Label>
                        <p className="text-sm text-slate-400 mt-1">
                          Automatically grant a trial to every new user who signs up
                        </p>
                      </div>
                      <Switch
                        checked={globalSettings.enabled}
                        onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, enabled: checked })}
                      />
                    </div>

                    {globalSettings.enabled && (
                      <>
                        {/* Trial Duration & Plan */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-slate-300">Trial Duration (Days)</Label>
                            <Select
                              value={String(globalSettings.trial_days)}
                              onValueChange={(v) => setGlobalSettings({ ...globalSettings, trial_days: parseInt(v) })}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1 Day</SelectItem>
                                <SelectItem value="3">3 Days</SelectItem>
                                <SelectItem value="7">7 Days (1 Week)</SelectItem>
                                <SelectItem value="14">14 Days (2 Weeks)</SelectItem>
                                <SelectItem value="30">30 Days (1 Month)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-slate-300">Trial Plan Level</Label>
                            <Select
                              value={globalSettings.plan}
                              onValueChange={(v) => setGlobalSettings({ ...globalSettings, plan: v })}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free</SelectItem>
                                <SelectItem value="standard">Standard</SelectItem>
                                <SelectItem value="premium">Premium</SelectItem>
                                <SelectItem value="pro">Pro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Video Limits */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-slate-300">Video Limit</Label>
                            <Select
                              value={String(globalSettings.video_limit)}
                              onValueChange={(v) => setGlobalSettings({ ...globalSettings, video_limit: parseInt(v) })}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">1 Video</SelectItem>
                                <SelectItem value="3">3 Videos</SelectItem>
                                <SelectItem value="5">5 Videos</SelectItem>
                                <SelectItem value="10">10 Videos</SelectItem>
                                <SelectItem value="20">20 Videos</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-slate-300">Max Video Duration</Label>
                            <Select
                              value={String(globalSettings.max_duration)}
                              onValueChange={(v) => setGlobalSettings({ ...globalSettings, max_duration: parseInt(v) })}
                            >
                              <SelectTrigger className="bg-slate-900 border-slate-600">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="10">10 Seconds</SelectItem>
                                <SelectItem value="15">15 Seconds</SelectItem>
                                <SelectItem value="30">30 Seconds</SelectItem>
                                <SelectItem value="60">60 Seconds</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Feature Access */}
                        <div className="space-y-3">
                          <Label className="text-slate-300">Feature Access</Label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                              <span className="text-sm text-white">UGC Ad Studio</span>
                              <Switch
                                checked={globalSettings.ugc_access}
                                onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, ugc_access: checked })}
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                              <span className="text-sm text-white">Video Captions</span>
                              <Switch
                                checked={globalSettings.caption_access}
                                onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, caption_access: checked })}
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                              <span className="text-sm text-white">Ghost Mode (Viral Ghosting)</span>
                              <Switch
                                checked={globalSettings.ghost_mode_access}
                                onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, ghost_mode_access: checked })}
                              />
                            </div>
                            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                              <span className="text-sm text-white">Stock Footage (Pexels)</span>
                              <Switch
                                checked={globalSettings.stock_footage_access}
                                onCheckedChange={(checked) => setGlobalSettings({ ...globalSettings, stock_footage_access: checked })}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Welcome Message */}
                        <div className="space-y-2">
                          <Label className="text-slate-300">Welcome Message (shown to new users)</Label>
                          <Input
                            value={globalSettings.welcome_message}
                            onChange={(e) => setGlobalSettings({ ...globalSettings, welcome_message: e.target.value })}
                            placeholder="Welcome! Enjoy your free trial."
                            className="bg-slate-900 border-slate-600"
                          />
                        </div>
                      </>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end pt-4 border-t border-slate-700">
                      <Button
                        onClick={saveGlobalSettings}
                        disabled={settingsSaving}
                        className="bg-cyan-500 hover:bg-cyan-600"
                      >
                        {settingsSaving ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Save Settings
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Current Settings Summary */}
                    {globalSettings.enabled && (
                      <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                        <h4 className="text-cyan-400 font-medium mb-2">Current Trial Configuration</h4>
                        <p className="text-sm text-slate-300">
                          New users will automatically receive a <strong>{globalSettings.trial_days}-day</strong> trial 
                          with the <strong className="capitalize">{globalSettings.plan}</strong> plan, 
                          allowing <strong>{globalSettings.video_limit} videos</strong> up to <strong>{globalSettings.max_duration}s</strong> each.
                          {globalSettings.ugc_access && " UGC Ad Studio included."}
                          {globalSettings.caption_access && " Captions included."}
                          {globalSettings.ghost_mode_access && " Ghost Mode included."}
                          {globalSettings.stock_footage_access && " Stock Footage included."}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trial Grants Tab */}
          <TabsContent value="trials">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-white">Trial Grants</CardTitle>
                  <CardDescription>View and manage all trial grants</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-32 bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                      <SelectItem value="revoked">Revoked</SelectItem>
                      <SelectItem value="superseded">Superseded</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => loadTrials()}
                    className="border-slate-600"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {trials.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No trial grants found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {trials.map((trial) => (
                      <div
                        key={trial.id}
                        className="p-4 bg-slate-700/50 rounded-lg border border-slate-600 hover:border-slate-500 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <button 
                                onClick={() => handleViewUserProfileByEmail(trial.user_email)}
                                className="text-white font-medium hover:text-cyan-400 transition-colors flex items-center gap-1"
                              >
                                <User className="w-3 h-3" />
                                {trial.user_email || trial.user_id}
                              </button>
                              {getStatusBadge(trial.status, trial.expires_at)}
                              {trial.grant_type === "package_trial" && (
                                <Badge variant="outline" className="text-purple-400 border-purple-500/30">
                                  <Package className="w-3 h-3 mr-1" />
                                  {trial.trial_plan_name}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-400">
                              <span>{trial.video_limit} videos</span>
                              <span>{trial.max_duration}s max</span>
                              {trial.ugc_access && (
                                <span className="text-cyan-400">+ UGC Access</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span>Expires: {formatDate(trial.expires_at)}</span>
                              {trial.reason && <span>Reason: {trial.reason}</span>}
                            </div>
                          </div>
                          
                          {/* Quick Actions */}
                          <div className="flex items-center gap-2">
                            {/* Reset - Available for active trials */}
                            {trial.status === "active" && new Date(trial.expires_at) > new Date() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetTrial(trial.user_email, 0)}
                                className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                                title="Reset video count"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {/* Renew - Available for all trials */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRenewTrial(trial.user_email, {
                                video_limit: trial.video_limit,
                                max_duration: trial.max_duration,
                                plan: trial.trial_plan_name || "premium",
                                ugc_access: trial.ugc_access
                              })}
                              className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                              title="Renew trial"
                            >
                              <Zap className="w-4 h-4" />
                            </Button>
                            
                            {/* Revoke - Only for active trials */}
                            {trial.status === "active" && new Date(trial.expires_at) > new Date() && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokeTrial(trial.user_email)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                title="Revoke trial"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Lookup Tab */}
          <TabsContent value="lookup">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">User Lookup</CardTitle>
                <CardDescription>Search for a user by email or username to view their subscription and trial history</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email or username..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearchUser()}
                    className="bg-slate-700 border-slate-600"
                    data-testid="user-lookup-input"
                  />
                  <Button
                    onClick={() => handleSearchUser()}
                    disabled={searchLoading}
                    className="bg-cyan-500 hover:bg-cyan-600 text-black"
                    data-testid="user-lookup-search-btn"
                  >
                    {searchLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>

                {userDetails && (
                  <div className="space-y-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => handleViewUserProfileByEmail(userDetails.user_email)}
                        className="text-left hover:bg-slate-700/50 rounded-lg p-2 -m-2 transition-colors"
                      >
                        <h3 className="text-white font-medium hover:text-cyan-400 transition-colors flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {userDetails.user_name || userDetails.user_email}
                        </h3>
                        <p className="text-cyan-400 text-sm">{userDetails.user_email}</p>
                      </button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setGrantForm(prev => ({ ...prev, user_email: userDetails.user_email }));
                          setGrantModalOpen(true);
                        }}
                        className="bg-cyan-500 hover:bg-cyan-600 text-black"
                      >
                        <Gift className="w-4 h-4 mr-1" />
                        Grant Trial
                      </Button>
                    </div>

                    {/* Current Subscription */}
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-white font-medium mb-2">Current Subscription</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-400">Plan</span>
                          <p className="text-white">{userDetails.current_subscription?.plan_name || "Free"}</p>
                        </div>
                        <div>
                          <span className="text-slate-400">Videos Used</span>
                          <p className="text-white">
                            {userDetails.current_subscription?.videos_used || 0} / {userDetails.current_subscription?.video_limit || 1}
                          </p>
                        </div>
                        <div>
                          <span className="text-slate-400">Max Duration</span>
                          <p className="text-white">{userDetails.current_subscription?.max_duration || 10}s</p>
                        </div>
                        <div>
                          <span className="text-slate-400">UGC Access</span>
                          <p className={userDetails.current_subscription?.ugc_access ? "text-green-400" : "text-slate-400"}>
                            {userDetails.current_subscription?.ugc_access ? "Yes" : "No"}
                          </p>
                        </div>
                      </div>
                      {userDetails.current_subscription?.has_trial_grant && (
                        <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                          <p className="text-cyan-400 text-sm font-medium mb-2">Active Trial - Quick Actions</p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetTrial(userDetails.user_email, 0)}
                              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reset Count
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetTrial(userDetails.user_email, 7)}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                            >
                              <Calendar className="w-4 h-4 mr-1" />
                              +7 Days
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRenewTrial(userDetails.user_email)}
                              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                            >
                              <Zap className="w-4 h-4 mr-1" />
                              Renew
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRevokeTrial(userDetails.user_email)}
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          </div>
                        </div>
                      )}
                      {!userDetails.current_subscription?.has_trial_grant && (
                        <Badge className="mt-2 bg-slate-500/20 text-slate-400 border-slate-500/30">
                          No Active Trial
                        </Badge>
                      )}
                    </div>

                    {/* Trial History */}
                    {userDetails.trial_history && userDetails.trial_history.length > 0 && (
                      <div>
                        <h4 className="text-white font-medium mb-2">Trial History</h4>
                        <div className="space-y-2">
                          {userDetails.trial_history.map((trial) => (
                            <div
                              key={trial.id}
                              className="p-3 bg-slate-700/30 rounded-lg text-sm"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(trial.status, trial.expires_at)}
                                  <span className="text-slate-300">
                                    {trial.video_limit} videos, {trial.max_duration}s
                                    {trial.ugc_access && " + UGC"}
                                  </span>
                                </div>
                                <span className="text-slate-500 text-xs">
                                  {formatDate(trial.granted_at)} → {formatDate(trial.expires_at)}
                                </span>
                              </div>
                              {trial.reason && (
                                <p className="text-slate-500 text-xs mt-1">Reason: {trial.reason}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Grant Trial Modal */}
      <Dialog open={grantModalOpen} onOpenChange={setGrantModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-cyan-400" />
              Grant Trial Access
            </DialogTitle>
            <DialogDescription>
              Grant free trial access to a user for marketing or support purposes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Grant Type Selection */}
            <div className="flex gap-2">
              <Button
                variant={grantType === "extended" ? "default" : "outline"}
                onClick={() => setGrantType("extended")}
                className={grantType === "extended" ? "bg-cyan-500 text-black" : "border-slate-600"}
              >
                <Zap className="w-4 h-4 mr-1" />
                Custom Trial
              </Button>
              <Button
                variant={grantType === "package" ? "default" : "outline"}
                onClick={() => setGrantType("package")}
                className={grantType === "package" ? "bg-purple-500 text-white" : "border-slate-600"}
              >
                <Crown className="w-4 h-4 mr-1" />
                Package Trial
              </Button>
            </div>

            {/* User Email */}
            <div className="space-y-2">
              <Label className="text-slate-300">User Email</Label>
              <Input
                placeholder="user@example.com"
                value={grantForm.user_email}
                onChange={(e) => setGrantForm(prev => ({ ...prev, user_email: e.target.value }))}
                className="bg-slate-800 border-slate-600"
              />
            </div>

            {grantType === "extended" ? (
              <>
                {/* Video Limit */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Video Limit</Label>
                  <Input
                    type="number"
                    value={grantForm.video_limit}
                    onChange={(e) => setGrantForm(prev => ({ ...prev, video_limit: parseInt(e.target.value) || 1 }))}
                    className="bg-slate-800 border-slate-600"
                  />
                </div>

                {/* Max Duration */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Max Duration (seconds)</Label>
                  <Select
                    value={String(grantForm.max_duration)}
                    onValueChange={(v) => setGrantForm(prev => ({ ...prev, max_duration: parseInt(v) }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 seconds</SelectItem>
                      <SelectItem value="15">15 seconds</SelectItem>
                      <SelectItem value="30">30 seconds</SelectItem>
                      <SelectItem value="60">60 seconds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* UGC Access */}
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Include UGC Ad Studio</Label>
                  <Switch
                    checked={grantForm.ugc_access}
                    onCheckedChange={(checked) => setGrantForm(prev => ({ ...prev, ugc_access: checked }))}
                  />
                </div>
              </>
            ) : (
              <>
                {/* Package Selection */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Package to Grant</Label>
                  <Select
                    value={grantForm.plan}
                    onValueChange={(v) => setGrantForm(prev => ({ ...prev, plan: v }))}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (8 videos, 10s)</SelectItem>
                      <SelectItem value="premium">Premium (12 videos, 15s)</SelectItem>
                      <SelectItem value="pro">Pro (15 videos, 30s, UGC)</SelectItem>
                      <SelectItem value="enterprise">Enterprise (50 videos, 30s, UGC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Duration Days */}
            <div className="space-y-2">
              <Label className="text-slate-300">Trial Duration (days)</Label>
              <Select
                value={String(grantForm.duration_days)}
                onValueChange={(v) => setGrantForm(prev => ({ ...prev, duration_days: parseInt(v) }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label className="text-slate-300">Reason (optional)</Label>
              <Input
                placeholder="e.g., Influencer partnership, Customer support..."
                value={grantForm.reason}
                onChange={(e) => setGrantForm(prev => ({ ...prev, reason: e.target.value }))}
                className="bg-slate-800 border-slate-600"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGrantModalOpen(false)}
              className="border-slate-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGrantTrial}
              disabled={grantLoading}
              className="bg-cyan-500 hover:bg-cyan-600 text-black"
            >
              {grantLoading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
              Grant Trial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={userProfileOpen}
        onClose={() => setUserProfileOpen(false)}
        userId={selectedUserId}
      />
    </div>
  );
}
